import xhr from 'xhr';

/**
 * Obtiene los logs de auditoría con filtros y paginación
 * @param {Object} options - Opciones de búsqueda
 * @param {number} options.page - Número de página (default: 1)
 * @param {number} options.pageSize - Tamaño de página (default: 10)
 * @param {Object} options.filters - Filtros de búsqueda
 * @returns {Promise<{data: Array, meta: Object}>}
 */
export async function getAuditLogs({ page = 1, pageSize = 10, filters = {} } = {}) {
  const params = new URLSearchParams();
  
  // Paginación
  params.append('pagination[page]', page);
  params.append('pagination[pageSize]', pageSize);
  
  // Populate user para mostrar información del responsable
  params.append('populate[user][fields][0]', 'id');
  params.append('populate[user][fields][1]', 'first_name');
  params.append('populate[user][fields][2]', 'last_name');
  params.append('populate[user][fields][3]', 'email');
  
  // Ordenar por fecha descendente
  params.append('sort[0]', 'createdAt:desc');
  
  // Filtro de búsqueda (ramificado por modo)
  if (filters.search) {
    if (filters.searchMode === 'estudiante') {
      // Modo Estudiante: el backend resuelve por nombre/identidad/id del alumno
      params.append('studentSearch', filters.search);
    } else {
      // Modo Responsable (default): busca por usuario, ID de entidad o detalle
      params.append('filters[$or][0][summary][$containsi]', filters.search);
      params.append('filters[$or][1][entity_id][$containsi]', filters.search);
      params.append('filters[$or][2][user][first_name][$containsi]', filters.search);
      params.append('filters[$or][3][user][last_name][$containsi]', filters.search);
      params.append('filters[$or][4][user][email][$containsi]', filters.search);
    }
  }
  
  // Filtro por usuario
  if (filters.userId) {
    params.append('filters[user][id][$eq]', filters.userId);
  }
  
  // Filtro por acción
  if (filters.action) {
    params.append('filters[action][$eq]', filters.action);
  }
  
  // Filtro por tipo de entidad
  if (filters.entityType) {
    params.append('filters[entity_type][$eq]', filters.entityType);
  }
  
  // Filtro por rango de fechas
  if (filters.dateFrom) {
    params.append('filters[createdAt][$gte]', filters.dateFrom);
  }
  if (filters.dateTo) {
    params.append('filters[createdAt][$lte]', filters.dateTo);
  }
  
  const response = await xhr(`/audit-logs?${params.toString()}`);
  return response.data;
}

/**
 * Obtiene un log de auditoría por ID
 * @param {number} id - ID del log
 * @returns {Promise<Object>}
 */
export async function getAuditLogById(id) {
  const params = new URLSearchParams();
  params.append('populate[user][fields][0]', 'id');
  params.append('populate[user][fields][1]', 'first_name');
  params.append('populate[user][fields][2]', 'last_name');
  params.append('populate[user][fields][3]', 'email');
  
  const response = await xhr(`/audit-logs/${id}?${params.toString()}`);
  return response.data;
}

/**
 * Obtiene la lista de usuarios admin para el filtro
 * @returns {Promise<Array>}
 */
export async function getAdminUsers() {
  const response = await xhr(`/users?filters[role][type][$eq]=admin&fields[0]=id&fields[1]=first_name&fields[2]=last_name&fields[3]=email`);
  return response.data;
}

/**
 * Mapeo de acciones a labels para UI
 */
export const ACTION_LABELS = {
  'subject.bulk-dates': 'CAMBIAR FECHAS',
  'payment.approve': 'APROBAR',
  'grade.update': 'EDITAR',
  'enrollment.create': 'CREAR',
  'program.enroll': 'MATRICULAR',
  'enrollment.status': 'CAMBIAR',
  'enrollment.semester': 'CAMBIAR',
  'enrollment.group': 'ASIGNAR',
  'student.update': 'EDITAR',
  'student.activate': 'ACTIVAR',
  'student.deactivate': 'DESACTIVAR',
  'requirement.approve': 'APROBAR',
  'requirement.deny': 'BORRAR',
  'requirement-sitting.duplicate': 'DUPLICAR',
  'campaign.send': 'ENVIAR',
  'system.login': 'LOGIN',
  'constancy.create': 'GENERAR',
  'document.create': 'GENERAR',
  // Panel Monitoring › Calificación de actividades
  'ai-grading.pause-release': 'IA · PAUSAR',
  'ai-grading.resume-release': 'IA · REANUDAR',
  'ai-grading.pause-subjects': 'IA · PAUSAR',
  'ai-grading.resume-subjects': 'IA · REACTIVAR',
  'ai-grading.hold': 'IA · RETENER',
  'ai-grading.unhold': 'IA · LIBERAR',
  'ai-grading.regrade': 'IA · RECALIFICAR',
  'ai-grading.to-teacher': 'IA · AL DOCENTE',
  'ai-grading.request-review': 'IA · REVISIÓN',
  'ai-grading.revert-run': 'IA · REVERTIR',
  'ai-grading.activate': 'IA · ACTIVAR',
  'ai-grading.deactivate': 'IA · DESACTIVAR',
  'ai-grading.run-test': 'IA · PRUEBA',
  'ai-grading.publish-test': 'IA · PUBLICAR',
  'ai-grading.discard-test': 'IA · DESCARTAR',
  // Finanzas
  'finance-movement.create': 'FINANZAS · REGISTRAR',
  'finance-movement.update': 'FINANZAS · EDITAR',
  'finance-movement.void': 'FINANZAS · ANULAR',
  'finance-movement.transfer': 'FINANZAS · TRASLADO',
  'finance-movement.review': 'FINANZAS · REVISIÓN',
  'finance-movement.category': 'FINANZAS · CATEGORÍA',
  'finance-movement.bulk-category': 'FINANZAS · EN BLOQUE',
  'finance-movement.bulk-category-undo': 'FINANZAS · DESHACER',
  'finance-movement.attachment': 'FINANZAS · SOPORTE',
  'finance-period.save': 'FINANZAS · CAJA INICIAL',
  'finance-category.create': 'FINANZAS · CATEGORÍA',
  'finance-category.update': 'FINANZAS · CATEGORÍA',
  'finance-account.limits': 'FINANZAS · TOPES',
};

/**
 * Mapeo de acciones a colores de badge
 */
export const ACTION_COLORS = {
  'payment.approve': { bg: 'green.500', color: 'white', borderColor: 'green.500' },
  'grade.update': { bg: 'white', color: 'blue.500', borderColor: 'blue.500' },
  'enrollment.create': { bg: 'purple.500', color: 'white', borderColor: 'purple.500' },
  'program.enroll': { bg: 'green.600', color: 'white', borderColor: 'green.600' },
  'enrollment.status': { bg: 'cyan.500', color: 'white', borderColor: 'cyan.500' },
  'enrollment.semester': { bg: 'blue.500', color: 'white', borderColor: 'blue.500' },
  'enrollment.group': { bg: 'purple.400', color: 'white', borderColor: 'purple.400' },
  'student.update': { bg: 'white', color: 'blue.500', borderColor: 'blue.500' },
  'student.activate': { bg: 'green.500', color: 'white', borderColor: 'green.500' },
  'student.deactivate': { bg: 'red.500', color: 'white', borderColor: 'red.500' },
  'requirement.approve': { bg: 'green.500', color: 'white', borderColor: 'green.500' },
  'requirement.deny': { bg: 'orange.500', color: 'white', borderColor: 'orange.500' },
  'requirement-sitting.duplicate': { bg: 'blue.500', color: 'white', borderColor: 'blue.600' },
  'campaign.send': { bg: 'orange.400', color: 'white', borderColor: 'orange.400' },
  'system.login': { bg: 'white', color: 'gray.500', borderColor: 'gray.400' },
  'constancy.create': { bg: 'purple.500', color: 'white', borderColor: 'purple.500' },
  'document.create': { bg: 'teal.500', color: 'white', borderColor: 'teal.500' },
  'ai-grading.pause-release': { bg: 'orange.500', color: 'white', borderColor: 'orange.500' },
  'ai-grading.resume-release': { bg: 'green.500', color: 'white', borderColor: 'green.500' },
  'ai-grading.pause-subjects': { bg: 'orange.500', color: 'white', borderColor: 'orange.500' },
  'ai-grading.resume-subjects': { bg: 'green.500', color: 'white', borderColor: 'green.500' },
  'ai-grading.hold': { bg: 'purple.500', color: 'white', borderColor: 'purple.500' },
  'ai-grading.unhold': { bg: 'purple.400', color: 'white', borderColor: 'purple.400' },
  'ai-grading.regrade': { bg: 'blue.500', color: 'white', borderColor: 'blue.500' },
  'ai-grading.to-teacher': { bg: 'orange.400', color: 'white', borderColor: 'orange.400' },
  'ai-grading.request-review': { bg: 'cyan.500', color: 'white', borderColor: 'cyan.500' },
  'ai-grading.revert-run': { bg: 'red.500', color: 'white', borderColor: 'red.500' },
  'ai-grading.activate': { bg: 'green.600', color: 'white', borderColor: 'green.600' },
  'ai-grading.deactivate': { bg: 'red.400', color: 'white', borderColor: 'red.400' },
  'ai-grading.run-test': { bg: 'blue.400', color: 'white', borderColor: 'blue.400' },
  'ai-grading.publish-test': { bg: 'green.500', color: 'white', borderColor: 'green.500' },
  'ai-grading.discard-test': { bg: 'gray.500', color: 'white', borderColor: 'gray.500' },
  'finance-movement.create': { bg: 'purple.500', color: 'white', borderColor: 'purple.500' },
  'finance-movement.update': { bg: 'white', color: 'purple.600', borderColor: 'purple.500' },
  'finance-movement.void': { bg: 'red.500', color: 'white', borderColor: 'red.500' },
  'finance-movement.transfer': { bg: 'blue.500', color: 'white', borderColor: 'blue.500' },
  'finance-movement.review': { bg: 'orange.400', color: 'white', borderColor: 'orange.400' },
  'finance-movement.category': { bg: 'white', color: 'purple.600', borderColor: 'purple.300' },
  'finance-movement.bulk-category': { bg: 'purple.400', color: 'white', borderColor: 'purple.400' },
  'finance-movement.bulk-category-undo': { bg: 'gray.500', color: 'white', borderColor: 'gray.500' },
  'finance-movement.attachment': { bg: 'white', color: 'gray.600', borderColor: 'gray.400' },
  'finance-period.save': { bg: 'teal.500', color: 'white', borderColor: 'teal.500' },
  'finance-category.create': { bg: 'white', color: 'purple.600', borderColor: 'purple.300' },
  'finance-category.update': { bg: 'white', color: 'purple.600', borderColor: 'purple.300' },
  'finance-account.limits': { bg: 'white', color: 'teal.600', borderColor: 'teal.400' },
};

/**
 * Mapeo de entity_type a labels legibles
 */
export const ENTITY_TYPE_LABELS = {
  'api::payment.payment': 'Pago',
  'api::activity-sitting.activity-sitting': 'Notas',
  'api::subject-enrollment.subject-enrollment': 'Matrícula Materia',
  'api::grade-enrollment.grade-enrollment': 'Matrícula Programa',
  'api::technical-career-enrollment.technical-career-enrollment': 'Matrícula Carrera Técnica',
  'api::course-enrollment.course-enrollment': 'Matrícula Curso',
  'api::student-group.student-group': 'Grupo Estudiante',
  'api::student.student': 'Perfil de Estudiante',
  'api::requirement-sitting.requirement-sitting': 'Requisito',
  'api::email-campaign.email-campaign': 'Campaña Email',
  'api::student-constancy.student-constancy': 'Constancia',
  'api::student-document.student-document': 'Documento',
  'ai-grading': 'Calificación automática',
  'finance-movement': 'Finanzas · Movimiento',
  'finance-period': 'Finanzas · Caja inicial',
  'finance-category': 'Finanzas · Categoría',
  'finance-account': 'Finanzas · Cuenta',
};

/**
 * Opciones de filtro de fecha
 */
export const DATE_FILTER_OPTIONS = [
  { value: '7', label: 'Últimos 7 días' },
  { value: '30', label: 'Últimos 30 días' },
  { value: '90', label: 'Últimos 90 días' },
  { value: 'all', label: 'Todo' },
];

/**
 * Opciones de filtro de acción
 */
export const ACTION_FILTER_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'payment.approve', label: 'Aprobar Pago' },
  { value: 'grade.update', label: 'Actualizar Nota' },
  { value: 'enrollment.create', label: 'Matrícula Materia' },
  { value: 'program.enroll', label: 'Matrícula Programa' },
  { value: 'enrollment.status', label: 'Cambiar Estado Programa' },
  { value: 'subject.bulk-dates', label: 'Cambiar fechas de materias' },
  { value: 'enrollment.semester', label: 'Cambiar Semestre Programa' },
  { value: 'enrollment.group', label: 'Asignar Grupo' },
  { value: 'student.update', label: 'Editar Estudiante' },
  { value: 'student.activate', label: 'Activar Estudiante' },
  { value: 'student.deactivate', label: 'Desactivar Estudiante' },
  { value: 'requirement.approve', label: 'Aprobar Requisito' },
  { value: 'requirement.deny', label: 'Rechazar Requisito' },
  { value: 'requirement-sitting.duplicate', label: 'Duplicar requisito documental' },
  { value: 'campaign.send', label: 'Enviar Campaña' },
  { value: 'document.create', label: 'Generar Documento' },
  { value: 'ai-grading.pause-release', label: 'Calificación · Pausar publicación' },
  { value: 'ai-grading.resume-release', label: 'Calificación · Reanudar publicación' },
  { value: 'ai-grading.pause-subjects', label: 'Calificación · Pausar materias' },
  { value: 'ai-grading.resume-subjects', label: 'Calificación · Reactivar materias' },
  { value: 'ai-grading.hold', label: 'Calificación · Retener entrega' },
  { value: 'ai-grading.unhold', label: 'Calificación · Liberar entrega' },
  { value: 'ai-grading.regrade', label: 'Calificación · Volver a calificar' },
  { value: 'ai-grading.to-teacher', label: 'Calificación · Pasar al docente' },
  { value: 'ai-grading.request-review', label: 'Calificación · Enviar a revisión del docente' },
  { value: 'ai-grading.revert-run', label: 'Calificación · Revertir ejecución' },
  { value: 'ai-grading.activate', label: 'Calificación · Activar calificación' },
  { value: 'ai-grading.deactivate', label: 'Calificación · Desactivar calificación' },
  { value: 'ai-grading.run-test', label: 'Calificación · Correr prueba' },
  { value: 'ai-grading.publish-test', label: 'Calificación · Publicar prueba' },
  { value: 'ai-grading.discard-test', label: 'Calificación · Descartar prueba' },
  { value: 'finance-movement.create', label: 'Finanzas · Registrar movimiento' },
  { value: 'finance-movement.update', label: 'Finanzas · Editar movimiento' },
  { value: 'finance-movement.void', label: 'Finanzas · Anular movimiento' },
  { value: 'finance-movement.transfer', label: 'Finanzas · Traslado' },
  { value: 'finance-movement.review', label: 'Finanzas · Revisión del superadmin' },
  { value: 'finance-movement.bulk-category', label: 'Finanzas · Categoría en bloque' },
  { value: 'finance-period.save', label: 'Finanzas · Caja inicial' },
  { value: 'finance-category.create', label: 'Finanzas · Crear categoría' },
  { value: 'finance-category.update', label: 'Finanzas · Editar categoría' },
];
